/**
 * Easter cross-stitch patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterCrossStitchPatternsPack: ToilePack = {
    slug: 'easter-cross-stitch-patterns',
    category: 'season',
    name: 'Easter cross-stitch patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'egg', label: 'Egg chart', subject: 'A single patterned egg with a spiral and dot motif'},
        {key: 'bunny', label: 'Bunny chart', subject: 'A sitting bunny with long ears and a round tail'},
        {key: 'chick', label: 'Chick chart', subject: 'A fluffy chick standing beside a cracked eggshell'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a lamb instead', 'as a smaller chart', 'with a border pattern'],
    directionHint: 'try: a lamb instead',
    seo: {
        title: 'Easter cross stitch: egg, bunny, chick',
        metaDescription: 'Nine Easter cross-stitch charts — a patterned egg, a bunny, a chick — in simple, detailed or doodle stitch grids, gridded and keyed.',
        h1: 'Easter cross stitch patterns: egg, bunny and chick',
        tagline: 'Nine charts, three spring motifs, three grid styles each — ruled square by square with a key.',
        intro: "A cross-stitch chart for spring: a patterned egg, a sitting bunny, a fluffy chick beside a cracked shell, each charted three ways — bold simple symbols, a detailed dense grid, or a looser doodle-style chart. Every chart carries a symbol key in the corner. Print it and tape it to the frame, or ask for a different motif and it's charted again.",
        useCases: [
            'a chart taped to the embroidery frame',
            'a small Easter gift for a fellow stitcher',
            'a first project for a new stitcher',
            'a seasonal chart swapped by direction',
        ],
        faq: [
            {q: 'What count is the chart sized for?', a: 'The grid is drawn with a heavier rule every ten squares so counting stays easy, whichever of the three styles you pick.'},
            {q: 'Do I need the simple or the detailed chart?', a: 'Either works alone; the doodle-style chart is there for a looser, less exact stitch.'},
            {q: 'Can I get a different spring motif?', a: "Yes — use the direction field to ask for a different motif, like a lamb or a border pattern, and it's charted again in all three styles."},
        ],
    },
}
